/**
 * A customer's ledger from TallyPrime, read on request (0038; docs/product/statement/01-LEDGER-PLAN.md §8).
 *
 *   useLedgerAvailability   whether the Ledger is there for this account, and whether it can be read now —
 *                           asked only of a TallyPrime account; a database without 0038 has no Ledger
 *   useLedgerRequest        asking for a period, following the request until it ends, cancelling it
 *
 * Everything goes through the four functions for people (ledger_availability, ledger_request, ledger_read,
 * ledger_cancel), each of which checks, when it is called, that this person may see the customer. The ledger is
 * kept in this page's memory only: never in the address, the browser's storage or analytics.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LEDGER_END_WORDS, LEDGER_PERIOD_WORDS, type LedgerEnd, type LedgerLine, type LedgerPeriodProblem, type LedgerStatus } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';

export type LedgerReady = 'ready' | 'no_connection' | 'connector_too_old' | 'office_offline' | 'tally_closed' | 'company_not_loaded';

export interface LedgerAvailability {
    /** The Ledger tab: the company reads ledgers, this is a TallyPrime account, and its connection can answer. */
    available: boolean;
    ready: LedgerReady;
    lastSeenAt: string | null;
    /** The company's today, which a period may not end after. */
    today: string;
    booksFrom: string | null;
}

/** A ledger as it came back: TallyPrime's figures, checked by the connector and again by the server. */
export interface LedgerResult {
    from: string;
    to: string;
    openingPaise: number;
    closingPaise: number;
    tallyClosingPaise: number;
    /** The last update's balance for this account, or null where there is none to compare with. */
    syncedClosingPaise: number | null;
    lines: LedgerLine[];
    readAt: string;
}

export function useLedgerAvailability(workspaceId: string, customerId: string, tally: boolean) {
    const [availability, setAvailability] = useState<LedgerAvailability | null>(null);
    const [loading, setLoading] = useState(tally);
    const reload = useCallback(async () => {
        if (!tally) { setAvailability(null); setLoading(false); return; }
        const { data, error } = await supabase().rpc('ledger_availability', { p_workspace: workspaceId, p_customer: customerId });
        // No Ledger at all where it cannot be asked about: an older database, or an account that is not this person's.
        setAvailability(error || !data ? null : data as LedgerAvailability);
        setLoading(false);
    }, [workspaceId, customerId, tally]);
    useEffect(() => { void reload(); }, [reload]);
    return { availability, loading, reload };
}

export type LedgerPhase = 'idle' | 'asking' | 'done' | 'refused';

export interface LedgerRequestState {
    phase: LedgerPhase;
    request: string | null;
    status: LedgerStatus | null;
    /** While asking: whether the office computer has taken it ("reading") or not yet ("waiting"). */
    progress: 'waiting' | 'reading' | null;
    from: string | null;
    to: string | null;
    result: LedgerResult | null;
    /** Why there is no ledger to show, in words (a refusal before asking, or how the request ended). */
    problem: string | null;
}

const IDLE: LedgerRequestState = { phase: 'idle', request: null, status: null, progress: null, from: null, to: null, result: null, problem: null };

/** How often a waiting request is looked at. The request itself ends within five minutes, on the server's clock. */
export const LEDGER_POLL_MS = 1_500;

type Refusal = { code?: string; details?: string | null; message?: string };

/** A refusal from ledger_request, in the person's words. */
export function ledgerRefusalWords(error: Refusal, lastSeen: (iso: string) => string, lastSeenAt: string | null): string {
    const reason = error.details ?? '';
    if (error.code === '22023' && reason in LEDGER_PERIOD_WORDS) return LEDGER_PERIOD_WORDS[reason as LedgerPeriodProblem];
    switch (reason) {
        case 'office_offline':
            return lastSeenAt ? `The office computer was last in touch ${lastSeen(lastSeenAt)}, so the ledger can’t be read now.` : 'The office computer is not in touch, so the ledger can’t be read now.';
        case 'connector_too_old': return 'The ledger needs the newer Timely Connector on the office computer.';
        case 'no_connection': return 'TallyPrime is not connected to Timely Payment just now.';
        case 'not_enabled': return 'Ledgers are not read from TallyPrime for this company.';
        case 'not_a_tally_customer': return 'This account is not a TallyPrime ledger.';
        case 'rate_limited': return 'Too many ledgers were asked for in the last hour. Try again a little later.';
        default: break;
    }
    if (error.code === '42501') return 'You can’t read ledgers for this company.';
    if (error.code === 'P0002') return 'That account is not available.';
    return 'The ledger could not be asked for just now. Try again.';
}

export function useLedgerRequest(workspaceId: string, customerId: string, words: { lastSeen: (iso: string) => string; lastSeenAt: string | null }) {
    const [state, setState] = useState<LedgerRequestState>(IDLE);
    const timer = useRef<number | null>(null);
    const live = useRef(true);
    // The request being followed: an older one's answer, arriving late, never replaces a newer one's.
    const current = useRef<string | null>(null);
    const stop = () => { if (timer.current !== null) window.clearTimeout(timer.current); timer.current = null; };
    // Mounted again (React's development check mounts, unmounts and mounts once more): live again, so a request
    // already under way is still followed and its answer shown.
    useEffect(() => {
        live.current = true;
        return () => { live.current = false; stop(); };
    }, []);

    const follow = useCallback(async (request: string) => {
        if (!live.current || current.current !== request) return;
        const { data, error } = await supabase().rpc('ledger_read', { p_workspace: workspaceId, p_request: request });
        if (!live.current || current.current !== request) return;
        if (error || !data) {
            setState(s => ({ ...s, phase: 'refused', progress: null, problem: 'The ledger could not be read just now. Try again.' }));
            return;
        }
        const r = data as { status: LedgerStatus; outcome: string | null; from: string; to: string; answer: Omit<LedgerResult, 'from' | 'to'> | null; cleared: boolean };
        if (r.status === 'pending' || r.status === 'claimed') {
            setState(s => ({ ...s, status: r.status, progress: r.status === 'claimed' ? 'reading' : 'waiting' }));
            timer.current = window.setTimeout(() => { void follow(request); }, LEDGER_POLL_MS);
            return;
        }
        if (r.status === 'completed' && r.answer) {
            setState(s => ({ ...s, phase: 'done', status: r.status, progress: null, result: { ...r.answer!, from: r.from, to: r.to }, problem: null }));
            return;
        }
        const problem = r.status === 'completed'
            ? 'That ledger was read more than fifteen minutes ago and is no longer kept. Ask for it again.'
            : r.status === 'cancelled' ? 'Cancelled.'
                : LEDGER_END_WORDS[(r.outcome ?? 'refused') as LedgerEnd] ?? LEDGER_END_WORDS.refused;
        setState(s => ({ ...s, phase: 'refused', status: r.status, progress: null, result: null, problem }));
    }, [workspaceId]);

    const ask = useCallback(async (from: string, to: string) => {
        stop();
        current.current = null;
        setState({ ...IDLE, phase: 'asking', progress: 'waiting', from, to });
        const key = crypto.randomUUID();
        const { data, error } = await supabase().rpc('ledger_request', { p_workspace: workspaceId, p_customer: customerId, p_from: from, p_to: to, p_key: key });
        if (!live.current) return;
        if (error || !data) {
            setState({ ...IDLE, phase: 'refused', from, to, problem: ledgerRefusalWords(error ?? {}, words.lastSeen, words.lastSeenAt) });
            return;
        }
        const request = (data as { request: string }).request;
        current.current = request;
        setState(s => ({ ...s, request }));
        await follow(request);
    }, [workspaceId, customerId, follow, words.lastSeen, words.lastSeenAt]);

    const cancel = useCallback(async () => {
        const request = current.current;
        stop();
        current.current = null;
        if (request) await supabase().rpc('ledger_cancel', { p_workspace: workspaceId, p_request: request });
        if (live.current) setState(s => ({ ...IDLE, from: s.from, to: s.to, phase: 'refused', problem: 'Cancelled.' }));
    }, [workspaceId]);

    return { state, ask, cancel };
}
